import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IUseQueryStateProps } from '../interfaces/useQueryStateProps';
import { serializeValue } from '../utils/serializeValue';
import { BaseObj } from '../types/baseObj';

export const useQueryParamsState = <T extends BaseObj>({
                                                         state,
                                                         setState,
                                                         parser = {} as any,
                                                         wildcards = [],
                                                         arrays = [],
                                                       }: IUseQueryStateProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [prePopulated, setPrePopulated] = useState(false);

  // Init pre-populate query params into the state
  useEffect(() => {
    [...searchParams || []].forEach(([key, val]) => {
      if ((Object.keys(state).includes(key)) && state[key] !== val) {
        setState(prev => ({ ...prev, [key]: parser[key] ? parser[key](val) : getSerializedValue(val, state[key]) }));
      }
    });
    setPrePopulated(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const diff = useMemo(() => Object.entries(prePopulated ? state : {}).reduce((acc: { [key: string]: any }, [key, val]) => {
    const queryParamVal = searchParams.get(key);
    if (
      (queryParamVal !== val?.toString())
      || (queryParamVal && val === '')
    ) {
      acc[key] = val ? val.toString().trim() : val;
    }
    return acc;
  }, {}), [searchParams, state, prePopulated]);


  const handleChange = useCallback((to: [string, any][]) => {
    to.forEach(([key, val]) => ['', null, undefined].includes(val) ? searchParams.delete(key) : searchParams.set(key, val));
    setSearchParams(searchParams, { replace: true });
  }, [setSearchParams, searchParams]);

  useEffect(() => {
    if (Object.entries(diff).length) {
      handleChange(Object.entries(diff));
    }
  }, [diff, handleChange]);


  return useMemo<{ queryParamState: T, isPrePopulated: boolean }>(() => ({
    queryParamState: Object.entries(state).reduce((acc: T, [key, val]) => {
      const queryValue = searchParams.get(key);
      if (queryValue) {
        const value = wildcards.includes(key) ? queryValue + '%' : getSerializedValue(queryValue, state[key]);
        (acc as any)[key] = arrays.includes(key) && typeof value === 'string' ? value.split(',').map((v) => v.trim()) : value;
      }
      return acc;
    }, {} as T),
    isPrePopulated: prePopulated,
  }), [searchParams, state, wildcards, arrays, prePopulated]);
};

function getSerializedValue(value: string, stateValue: any) {
  return serializeValue(value, stateValue !== '' ? typeof stateValue : isNaN(+value) ? 'string' : 'number');
}